import { useId, useMemo, useState } from 'react';
import type { Granularity } from './types';

/**
 * The trend under one metric: this period solid, the one before it dashed.
 *
 * Deliberately not a charting library. These reports need one chart shape and
 * have to match a client's branding precisely; a library would mean fighting
 * its defaults for the same result, which is the problem we left Looker
 * Studio to escape. This is ~180 lines and adds nothing to the bundle.
 *
 * The viewBox is fixed and the SVG scales to its container, so the chart is
 * resolution-independent and needs no resize observer.
 */

const W = 420;
const H = 150;
const PAD = { top: 18, right: 8, bottom: 20, left: 40 };

const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

/** Above this, per-point value labels collide and are dropped. */
const MAX_LABELLED_POINTS = 14;

interface Props {
  buckets: string[];
  points: (number | null)[];
  previousPoints: (number | null)[];
  granularity: Granularity;
  /** False for standing totals, which are plotted against their own range. */
  zeroBased?: boolean;
  format: (value: number) => string;
  /** Compact form for the labels drawn on the chart itself. */
  formatShort: (value: number) => string;
}

interface Plotted {
  x: number;
  y: number;
  value: number;
  index: number;
}

/** A "nice" axis maximum, so gridlines land on round numbers. */
function niceCeiling(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalised = value / magnitude;
  const step = normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 5 ? 5 : 10;
  return step * magnitude;
}

export function bucketLabel(iso: string, granularity: Granularity): string {
  const d = new Date(`${iso}T00:00:00`);
  if (granularity === 'month') return d.toLocaleDateString(undefined, { month: 'short' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function TrendChart({
  buckets,
  points,
  previousPoints,
  granularity,
  zeroBased = true,
  format,
  formatShort,
}: Props) {
  const gradientId = useId();
  const [hover, setHover] = useState<number | null>(null);

  const { floor, ceiling, current, previous } = useMemo(() => {
    const all = [...points, ...previousPoints].filter((p): p is number => p !== null);
    const highest = Math.max(...all, 0);
    const lowest = Math.min(...all, highest);

    // A follower count that moves 2% in a quarter is a flat line when drawn
    // from zero. Giving it its own range shows the movement that is there,
    // and the axis labels say plainly where the range starts.
    const headroom = Math.max((highest - lowest) * 0.15, highest * 0.002, 1);
    const base = zeroBased ? 0 : Math.max(0, lowest - headroom);
    const top = zeroBased ? niceCeiling(highest) : highest + headroom;
    const span = top - base || 1;

    const n = buckets.length;
    const place = (values: (number | null)[]): Plotted[] =>
      values
        .map((value, index) =>
          value === null
            ? null
            : {
                // A single bucket sits in the middle rather than at x=0.
                x: n === 1 ? PLOT_W / 2 : (index / (n - 1)) * PLOT_W,
                y: PLOT_H - ((value - base) / span) * PLOT_H,
                value,
                index,
              },
        )
        .filter((p): p is Plotted => p !== null);

    return {
      floor: base,
      ceiling: top,
      current: place(points),
      previous: place(previousPoints),
    };
  }, [buckets, points, previousPoints, zeroBased]);

  const first = current.at(0);
  const last = current.at(-1);
  if (first === undefined || last === undefined) {
    return <p className="rp-empty">No data for this period.</p>;
  }

  const path = (pts: Plotted[]) =>
    pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  const labelled = buckets.length <= MAX_LABELLED_POINTS;
  // Dates collide long before the values do, so they get thinned separately:
  // roughly six across the axis, always including the last one.
  const dateStride = Math.max(1, Math.ceil(buckets.length / 6));
  const showDate = (index: number) =>
    index === buckets.length - 1 || (buckets.length - 1 - index) % dateStride === 0;
  const hoveredBucket = hover === null ? undefined : buckets[hover];
  const hoveredValue = hover === null ? undefined : current.find((p) => p.index === hover);
  const hoveredPrevious = hover === null ? undefined : previous.find((p) => p.index === hover);

  return (
    <div className="rp-trend">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Trend">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--rp-accent)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="var(--rp-accent)" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        <g transform={`translate(${PAD.left},${PAD.top})`}>
          {[0, 0.5, 1].map((fraction) => {
            const y = PLOT_H * fraction;
            return (
              <g key={fraction}>
                <line x1={0} x2={PLOT_W} y1={y} y2={y} className="rp-grid" />
                <text x={-8} y={y + 4} className="rp-axis" textAnchor="end">
                  {formatShort(floor + (ceiling - floor) * (1 - fraction))}
                </text>
              </g>
            );
          })}

          {previous.length > 0 && (
            <path d={path(previous)} className="rp-prev-line" fill="none" />
          )}

          {/* The fill reads as "this much"; with a non-zero axis it would be
              measuring from an arbitrary baseline, so it is dropped. */}
          {zeroBased && (
            <path
              d={`${path(current)} L${last.x},${PLOT_H} L${first.x},${PLOT_H} Z`}
              fill={`url(#${gradientId})`}
            />
          )}
          <path d={path(current)} className="rp-line" fill="none" />

          {labelled &&
            current.map((p) => (
              <text key={p.index} x={p.x} y={p.y - 7} className="rp-point" textAnchor="middle">
                {formatShort(p.value)}
              </text>
            ))}

          {hover !== null && hoveredValue && (
            <line
              x1={hoveredValue.x}
              x2={hoveredValue.x}
              y1={0}
              y2={PLOT_H}
              className="rp-crosshair"
            />
          )}

          {buckets.map((iso, index) => (
            <text
              key={iso}
              x={buckets.length === 1 ? PLOT_W / 2 : (index / (buckets.length - 1)) * PLOT_W}
              y={PLOT_H + 14}
              className="rp-axis"
              textAnchor={index === 0 ? 'start' : index === buckets.length - 1 ? 'end' : 'middle'}
            >
              {showDate(index) ? bucketLabel(iso, granularity) : ''}
            </text>
          ))}

          {/* One transparent band per bucket, so hovering reads the nearest
              point without any distance maths. */}
          {buckets.map((iso, index) => (
            <rect
              key={`hit-${iso}`}
              x={(index / buckets.length) * PLOT_W}
              y={0}
              width={PLOT_W / buckets.length}
              height={PLOT_H}
              fill="transparent"
              onMouseEnter={() => setHover(index)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
        </g>
      </svg>

      <p className="rp-readout">
        {hoveredBucket && hoveredValue ? (
          <>
            <strong>{bucketLabel(hoveredBucket, granularity)}</strong> {format(hoveredValue.value)}
            {hoveredPrevious && <em> · previous {format(hoveredPrevious.value)}</em>}
          </>
        ) : previous.length > 0 ? (
          <em>Dashed line: previous period</em>
        ) : (
          <em>&nbsp;</em>
        )}
      </p>
    </div>
  );
}
